import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AlertTriangle, CalendarDays, ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { formatCurrency } from '../../lib/formatters';
import { buildDigestView, formatDay } from './digestViewModel';

// Phase 6 Part 1 (10/06) — "Yesterday" card on the admin dashboard, fed by the
// daily snapshot portfolio_digest already writes to job_reports. Read-only.
// Tiles follow the Deals-page KPI pattern (big number, small uppercase label);
// accent #3b82f6 (Tailwind blue-500).

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Tile = ({ t }) => {
  const value = t.kind === 'money' ? formatCurrency(t.value) : t.value.toLocaleString('en-US');
  const accent = t.warn ? 'text-red-600' : 'text-gray-900';
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3" data-testid={`digest-tile-${t.key}`}>
      <p className={`text-2xl font-bold ${accent}`}>{value}</p>
      <p className="text-xs uppercase tracking-wide text-gray-500 mt-1">{t.label}</p>
      {t.sub && <p className="text-xs text-gray-400">{t.sub}</p>}
    </div>
  );
};

const DealLink = ({ dealId, children }) => (
  <Link to={`/deals/${dealId}`} className="text-[#3b82f6] hover:underline truncate">{children}</Link>
);

// YYYY-MM-DD arithmetic in UTC at noon so local time zones never shift a day.
const shiftDay = (iso, n) => {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

const PortfolioDigestCard = ({ getAuthHeaders, tenant = 'live' }) => {
  const [payload, setPayload] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);
  // 10/08 (Josh): pick any past day for a quick look. null = latest digest.
  const [pickedDate, setPickedDate] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const res = await axios.get(`${API}/reports/portfolio-digest`, {
          headers: { ...getAuthHeaders(), 'X-Tenant': tenant },
          params: pickedDate ? { date: pickedDate } : undefined,
        });
        if (!cancelled) setPayload(res.data);
      } catch (err) {
        if (!cancelled) {
          const detail = err.response?.data?.detail;
          setLoadError(typeof detail === 'string' ? detail : (err.message || 'request failed'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [getAuthHeaders, tenant, pickedDate]);

  // The day the picker shows: the chosen day, else the day of the loaded digest.
  const shownDay = pickedDate || payload?.report?.data?.day || payload?.report?.date || payload?.yesterday || '';
  // 10/08 (Josh): today is reachable like any other day (computed live by the API).
  const maxDay = payload?.today || payload?.yesterday || shownDay;
  const canGoForward = Boolean(shownDay) && Boolean(maxDay) && shownDay < maxDay;

  const header = (
    <CardHeader className="pb-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <CalendarDays className="h-4 w-4 text-[#3b82f6]" />
          {pickedDate ? (pickedDate === payload?.today ? 'Today (live)' : 'Digest') : 'Yesterday'}
          <span className="text-xs font-normal text-gray-500">portfolio digest</span>
        </CardTitle>
        <div className="flex items-center gap-1 text-xs" data-testid="digest-date-picker">
          <button type="button" aria-label="Previous day" title="Previous day"
            className="rounded border border-gray-200 px-1.5 py-1 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
            disabled={!shownDay} onClick={() => setPickedDate(shiftDay(shownDay, -1))}>
            <ChevronLeft className="h-3.5 w-3.5" />
          </button>
          <input type="date" value={shownDay} max={maxDay || undefined}
            onChange={(e) => setPickedDate(e.target.value || null)}
            className="h-7 rounded border border-gray-200 px-1.5 text-xs text-gray-700" aria-label="Digest day" />
          <button type="button" aria-label="Next day" title="Next day"
            className="rounded border border-gray-200 px-1.5 py-1 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
            disabled={!canGoForward} onClick={() => setPickedDate(shiftDay(shownDay, 1))}>
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
          {pickedDate && (
            <button type="button" onClick={() => setPickedDate(null)}
              className="ml-1 rounded border border-[#3b82f6] px-2 py-1 text-[#3b82f6] hover:bg-blue-50" data-testid="digest-latest">
              Latest
            </button>
          )}
        </div>
      </div>
    </CardHeader>
  );

  if (loading) {
    return (
      <Card className="mb-6" data-testid="portfolio-digest-card">
        {header}
        <CardContent><p className="text-sm text-gray-500">Loading yesterday's digest…</p></CardContent>
      </Card>
    );
  }

  if (loadError) {
    return (
      <Card className="mb-6" data-testid="portfolio-digest-card">
        {header}
        <CardContent>
          <div className="flex items-start gap-2 text-sm text-red-700">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <p>Couldn't load the digest: {loadError}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const view = buildDigestView(payload, { pinnedDate: pickedDate });

  if (view.mode === 'empty') {
    return (
      <Card className="mb-6" data-testid="portfolio-digest-card" data-state="empty">
        {header}
        <CardContent>
          <div className="flex items-start gap-2 text-sm text-gray-700">
            <AlertTriangle className="h-4 w-4 mt-0.5 text-amber-500 flex-shrink-0" />
            <div>
              <p className="font-medium">{view.title}</p>
              <p className="text-gray-500">{view.detail}</p>
              {view.link && (
                <Link to={view.link} className="text-[#3b82f6] hover:underline">Open Admin → Jobs</Link>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mb-6" data-testid="portfolio-digest-card" data-state={view.stale ? 'stale' : 'ok'}>
      {header}
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
          <span className="text-gray-700">
            For <span className="font-medium">{view.dayLabel}</span>
            <span className="text-gray-400"> ({view.day})</span>
          </span>
          {view.stale && (
            <span className="inline-flex items-center gap-1 rounded-md bg-amber-50 px-2 py-0.5 text-xs text-amber-800 border border-amber-200" data-testid="digest-stale">
              <Clock className="h-3 w-3" />
              Stale — expected {formatDay(view.expectedDay)}.{' '}
              <Link to="/admin/jobs" className="underline">Check the job</Link>
            </span>
          )}
          {view.live && (
            <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-0.5 text-xs text-emerald-800 border border-emerald-200" data-testid="digest-live">
              <Clock className="h-3 w-3" />
              Live — today so far. Figures move as payments post; the snapshot is written tomorrow at 7:45 AM ET.
            </span>
          )}
          {view.awaitingRun && !view.stale && (
            <span className="inline-flex items-center gap-1 rounded-md bg-blue-50 px-2 py-0.5 text-xs text-blue-800 border border-blue-200" data-testid="digest-pending">
              <Clock className="h-3 w-3" />
              Latest completed day. The next digest
              {view.nextRunAt ? ` runs ${new Date(view.nextRunAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' })} NY` : ' runs this morning'}.
            </span>
          )}
          {view.paused && (
            <span className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-2 py-0.5 text-xs text-gray-700 border border-gray-200">
              Job is disabled — this will not refresh.{' '}
              <Link to="/admin/jobs" className="underline">Admin → Jobs</Link>
            </span>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {view.headline.map((t) => <Tile key={t.key} t={t} />)}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-3">
            <p className="text-xs uppercase tracking-wide text-gray-500">Active portfolio</p>
            <div className="grid grid-cols-2 gap-3">
              {view.active.map((t) => <Tile key={t.key} t={t} />)}
            </div>
            <p className="text-xs uppercase tracking-wide text-gray-500 pt-1">Active balances by status</p>
            {view.byStatus.length === 0 ? (
              <p className="text-sm text-gray-400">No active statuses in this digest.</p>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {view.byStatus.map((r) => (
                    <tr key={r.status} className="border-t border-gray-100">
                      <td className="py-1 pr-2 text-gray-700">{r.status}</td>
                      <td className="py-1 pr-2 text-right text-gray-500 tabular-nums">{r.deals}</td>
                      <td className="py-1 text-right text-gray-900 tabular-nums">{formatCurrency(r.balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div>
            <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">Top movers</p>
            {view.movers.length === 0 ? (
              <p className="text-sm text-gray-400">No payment activity on {view.day}.</p>
            ) : (
              <ul className="space-y-1 text-sm">
                {view.movers.map((m) => (
                  <li key={m.dealId} className="flex items-center justify-between gap-2">
                    <DealLink dealId={m.dealId}>{m.deal}</DealLink>
                    <span className={`tabular-nums ${m.net < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                      {m.net < 0 ? '-' : '+'}{formatCurrency(Math.abs(m.net))}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">Funded</p>
            {view.funded.length === 0 ? (
              <p className="text-sm text-gray-400">No deals funded on {view.day}.</p>
            ) : (
              <ul className="space-y-1 text-sm">
                {view.funded.map((f) => (
                  <li key={f.dealId} className="flex items-center justify-between gap-2">
                    <DealLink dealId={f.dealId}>{f.deal}</DealLink>
                    <span className="tabular-nums text-gray-900">{formatCurrency(f.amount)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PortfolioDigestCard;
